import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, fmtMoney, fmtDate } from "@/lib/api";
import { PageHeader, PageBody } from "@/components/common/PageHeader";
import FilterBar from "@/components/common/FilterBar";
import EmptyState from "@/components/common/EmptyState";
import StatusBadge from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { Plus, Receipt, Download, X } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useSortableList, SortHeader } from "@/components/common/useSortableList";
import { toast } from "sonner";

export default function Invoices({ docType = "invoice", title = "Invoices", kicker = "SALES", subtitle = "All sales invoices and their lifecycle.", newPath = "/sales/invoices/new" }) {
    const [params] = useSearchParams();
    const initialStatus = params.get("status") || "";
    const [items, setItems] = useState([]);
    const [search, setSearch] = useState("");
    const [status, setStatus] = useState(initialStatus);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    const { sorted, sortKey, sortDir, onSort, selected, selectedIds, toggle, clearSelected, allSelected, toggleAll } = useSortableList(items, {
        storageKey: `invoices-${docType}`,
        initial: { key: "date", dir: "desc" },
        search,
        searchKeys: ["number", "customer_name"],
    });

    useEffect(() => {
        (async () => {
            setLoading(true);
            const { data } = await api.get("/invoices", { params: { type: docType, status: status || undefined } });
            setItems(data); setLoading(false);
        })();
    }, [status, docType]);

    const exportCsv = () => {
        const subset = selectedIds.length ? sorted.filter((i) => selected[i.id]) : sorted;
        if (!subset.length) return toast.error("Nothing to export");
        const cols = ["number", "customer_name", "date", "due_date", "total", "balance_due", "status", "currency"];
        const lines = [cols.join(",")];
        subset.forEach((r) => lines.push(cols.map((c) => JSON.stringify(r[c] ?? "")).join(",")));
        const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `${docType}-export-${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(a.href);
        toast.success(`Exported ${subset.length} rows`);
    };

    return (
        <>
            <PageHeader
                kicker={kicker}
                title={title}
                subtitle={subtitle}
                actions={<Button onClick={() => navigate(newPath)} data-testid="invoice-new"><Plus className="w-4 h-4 mr-1.5" />New {docType.replace("_", " ")}</Button>}
            />
            <PageBody>
                <FilterBar
                    search={search}
                    onSearch={setSearch}
                    activeCount={(search ? 1 : 0) + (status ? 1 : 0)}
                    onClear={() => setStatus("")}
                >
                    <Select value={status || "all"} onValueChange={(v) => setStatus(v === "all" ? "" : v)}>
                        <SelectTrigger className="h-9 w-[160px]" data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All statuses</SelectItem>
                            <SelectItem value="draft">Draft</SelectItem>
                            <SelectItem value="sent">Sent</SelectItem>
                            <SelectItem value="partial">Partial</SelectItem>
                            <SelectItem value="paid">Paid</SelectItem>
                            <SelectItem value="overdue">Overdue</SelectItem>
                            <SelectItem value="void">Void</SelectItem>
                        </SelectContent>
                    </Select>
                </FilterBar>

                {selectedIds.length > 0 && (
                    <div className="bg-foreground text-background flex items-center justify-between px-4 py-2.5 mb-3" style={{ borderRadius: 2 }} data-testid="bulk-toolbar">
                        <div className="text-[13px] font-medium">
                            {selectedIds.length} selected
                            <button onClick={clearSelected} className="ml-3 text-[12px] opacity-70 hover:opacity-100 inline-flex items-center gap-1" data-testid="bulk-clear">
                                <X className="w-3 h-3" /> Clear
                            </button>
                        </div>
                        <Button variant="secondary" size="sm" onClick={exportCsv} data-testid="bulk-export">
                            <Download className="w-3.5 h-3.5 mr-1.5" /> Export CSV
                        </Button>
                    </div>
                )}

                <div className="bg-card border border-border overflow-hidden" style={{ borderRadius: 2 }}>
                    {!loading && sorted.length > 0 && (
                        <div className="px-4 py-2 border-b border-border flex items-center justify-between bg-muted/30">
                            <div className="text-[10.5px] uppercase tracking-[0.16em] text-muted-foreground font-mono">
                                <span className="text-gold">●</span> {sorted.length} {sorted.length === 1 ? "record" : "records"}
                                {selectedIds.length > 0 && <span className="ml-3 text-foreground">· {selectedIds.length} selected</span>}
                            </div>
                            <div className="text-[10.5px] uppercase tracking-[0.16em] text-muted-foreground font-mono tabular-nums">
                                TOTAL <span className="text-foreground">{fmtMoney(sorted.reduce((s, r) => s + (r.total || 0), 0), sorted[0]?.currency || "INR")}</span>
                            </div>
                        </div>
                    )}
                    {loading ? <div className="p-8 text-center text-muted-foreground font-mono text-[12px]">{`> loading data…`}</div> :
                        sorted.length === 0 ? <EmptyState icon={Receipt} title="No documents" description="Create your first to get started." actionLabel="Create" onAction={() => navigate(newPath)} /> :
                            <table className="fin-table">
                                <thead><tr>
                                    <th style={{ width: 32 }}>
                                        <input type="checkbox" checked={allSelected} onChange={toggleAll} data-testid="select-all" />
                                    </th>
                                    <SortHeader label="Number" sortKey="number" currentKey={sortKey} dir={sortDir} onSort={onSort} />
                                    <SortHeader label="Customer" sortKey="customer_name" currentKey={sortKey} dir={sortDir} onSort={onSort} />
                                    <SortHeader label="Date" sortKey="date" currentKey={sortKey} dir={sortDir} onSort={onSort} />
                                    <SortHeader label="Due" sortKey="due_date" currentKey={sortKey} dir={sortDir} onSort={onSort} />
                                    <SortHeader label="Amount" sortKey="total" currentKey={sortKey} dir={sortDir} onSort={onSort} align="right" />
                                    <SortHeader label="Balance" sortKey="balance_due" currentKey={sortKey} dir={sortDir} onSort={onSort} align="right" />
                                    <SortHeader label="Status" sortKey="status" currentKey={sortKey} dir={sortDir} onSort={onSort} />
                                </tr></thead>
                                <tbody>
                                    {sorted.map((i) => (
                                        <tr key={i.id} className="cursor-pointer" data-testid={`invoice-row-${i.id}`}
                                            onClick={(e) => { if (!e.target.closest("input")) navigate(`/sales/invoices/${i.id}`); }}>
                                            <td onClick={(e) => e.stopPropagation()}>
                                                <input type="checkbox" checked={!!selected[i.id]} onChange={() => toggle(i.id)} data-testid={`select-${i.id}`} />
                                            </td>
                                            <td className="font-mono text-[12.5px] font-medium">{i.number}</td>
                                            <td className="truncate max-w-[220px]">{i.customer_name}</td>
                                            <td className="text-[12px] text-muted-foreground">{fmtDate(i.date)}</td>
                                            <td className="text-[12px] text-muted-foreground">{fmtDate(i.due_date)}</td>
                                            <td className="num">{fmtMoney(i.total, i.currency)}</td>
                                            <td className="num">{fmtMoney(i.balance_due, i.currency)}</td>
                                            <td><StatusBadge status={i.status} /></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>}
                </div>
            </PageBody>
        </>
    );
}
